import { createEntityService, currentUser, problemCardsService, roadmapItemsService, evidenceService, okrsService, hypothesesService, issuesService, reminders, timeEntriesService } from './database';
import { prepareRecord, problemIds } from '../lib/entitySchema';

export const services = {
  problems: problemCardsService, initiatives: roadmapItemsService, evidence: evidenceService,
  objectives: okrsService, hypotheses: hypothesesService, issues: issuesService,
  decisions: createEntityService('decisions'), experiments: createEntityService('experiments'),
  results: createEntityService('experiment_results'), learnings: createEntityService('learnings'),
  inbox: createEntityService('inbox_items'), feedback: createEntityService('insight_feedback'),
  imports: createEntityService('source_records'), insights: createEntityService('workspace_insights'),
  time: timeEntriesService, reminders,
};
export const emptyBrain = () => Object.fromEntries(Object.keys(services).map(key => [key, []]));
const workspaces = createEntityService('workspaces');
let workspaceFlight;
export async function ensureWorkspace() {
  const user = await currentUser();
  if (workspaceFlight?.userId === user.id) return workspaceFlight.promise;
  const promise = (async () => {
    const existing = await workspaces.getById(user.id);
    return existing || workspaces.create({ id: user.id, name: 'My product workspace', role: 'owner' });
  })();
  workspaceFlight = { userId: user.id, promise };
  try { return await promise; } finally { if (workspaceFlight?.promise === promise) workspaceFlight = null; }
}

let onboardingWrites = Promise.resolve();
// Writes run one at a time from a fresh read, so concurrent saves cannot drop each other's fields.
export function updateOnboarding(change) {
  const run = onboardingWrites.then(async () => {
    const workspace = await ensureWorkspace();
    const onboarding = change(workspace.onboarding || {});
    await workspaces.update(workspace.id, { onboarding });
    return onboarding;
  });
  onboardingWrites = run.catch(() => {});
  return run;
}
export const saveOnboarding = patch => updateOnboarding(onboarding => ({ ...onboarding, ...patch }));

export async function loadBrain() {
  const data = emptyBrain();
  const errors = {};
  await Promise.all(Object.entries(services).map(async ([name, service]) => {
    try { data[name] = await service.list(name === 'issues' ? '-updated_date' : '-created_at'); }
    catch (error) { errors[name] = error.message || `Could not load ${name}`; }
  }));
  return { data, errors };
}

// A retry uses the same ID. An interrupted handoff can be resumed without
// creating a second initiative; the problem is never marked before persistence.
export async function createInitiative(form, sourceProblem = null, dependencies = {}) {
  const initiatives = dependencies.initiatives || roadmapItemsService;
  const problems = dependencies.problems || problemCardsService;
  if (sourceProblem && !['validated','in_roadmap'].includes(sourceProblem.status)) throw new Error('Validate the problem before creating its initiative.');
  if (!form.title?.trim()) throw new Error('Give the initiative a title.');
  const payload = prepareRecord('roadmap_items', {
    ...form, title: form.title.trim(), id: form.id || crypto.randomUUID(),
    ...(sourceProblem ? { problem_id: sourceProblem.id, status: 'draft' } : {}),
  });
  const existing = await initiatives.getById(payload.id);
  const initiative = existing || await initiatives.create(payload);
  if (sourceProblem) await problems.update(sourceProblem.id, { status: 'in_roadmap' });
  return initiative;
}

export async function ensureInitiativeReminder(item) {
  if (!item.target_date || item.status === 'done') return;
  const existing = await reminders.getRemindersForRoadmapItem(item.id);
  const automatic = existing.find(r => r.reminder_type === 'initiative_due');
  if (automatic) {
    if (automatic.target_date !== item.target_date) await reminders.update(automatic.id, { target_date: item.target_date, due_at: null, snoozed_until: null, is_sent: false, delivered_channels: [], delivery_error: null, status: 'active' });
  } else if (!existing.length) {
    await reminders.create({ roadmap_item_id: item.id, title: `Review ${item.title}`, reminder_type: 'initiative_due', target_date: item.target_date, reminder_days_before: 7, notification_channels: ['in_app'], status: 'active' });
  }
}

export function relatedEvidence(initiative, evidence) {
  return evidence.filter(e => problemIds(initiative).includes(String(e.problem_card_id)));
}
